import {
  AbsoluteCenter,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  Icon,
  IconButton,
  Input,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import type { DropzoneOptions } from 'dropzone'
import Dropzone from 'dropzone'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { IoIosArrowRoundBack } from 'react-icons/io'
import useFetch, { CachePolicies } from 'use-http'
import { SemanticSearchImageSearch } from '../../../../graphics/media-library/SemanticSearchImageSearch'
import { MLSortParams } from '../../../constants'
import type { UploadPresignedURLResponse } from '../../../interfaces'
import { UserContext } from '../../../providers'
import { MediaLibraryActions, MLContext } from '../../../providers/MLProvider'

export const SemanticImageSearch: React.FC<{
  setSearchFocused(search: boolean): void
}> = ({ setSearchFocused }) => {
  const { t } = useTranslation()
  const dropzoneInstance = useRef<Dropzone | null>(null)
  const abortController = useRef(new AbortController())
  const {
    isOpen: isDropping,
    onOpen: onDropping,
    onClose: offDropping,
  } = useDisclosure()

  const { user } = useContext(UserContext)

  const { get } = useFetch<UploadPresignedURLResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
    signal: abortController.current.signal,
  })

  const [{ semanticUrl }, dispatch] = useContext(MLContext)

  const [url, setUrl] = useState<string>(semanticUrl)

  const [uploadState, setUploadState] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle')

  const [uploadError, setUploadError] = useState<string | null>(null)

  const initializeDropzone = useCallback(() => {
    if (dropzoneInstance.current) {
      dropzoneInstance.current.destroy()
    } // destroy previous dropzone instance if any

    const dropzoneOptions: DropzoneOptions = {
      url: 'some_random_url', // will never be used
      previewsContainer: false,
      autoProcessQueue: false,
      clickable: true,
      maxFiles: 1,
      uploadMultiple: false,
      acceptedFiles: 'image/*',
      dictInvalidFileType: 'You can only upload images.',
      async addedfile(this: Dropzone, f) {
        try {
          /**
           * This is a hack to run the dropzone's accept method before the addedfile event
           * We need to do this because the accept method is triggered after the
           * addedfile event, so file.accepted is not set yet
           *
           * Ref: https://github.com/dropzone/dropzone/blob/v6.0.0-beta.1/src/dropzone.js#L761-L794
           */
          await new Promise<void>((resolve, reject) => {
            // eslint-disable-next-line react/no-this-in-sfc
            this.accept(f, err => {
              if (err) {
                reject(err)
              } else {
                resolve()
              }
            })
          })
          setUploadState('loading')
          dropzoneInstance.current?.disable()
          const presignedUrlResponse = await get(
            `/api/media-library/${user?.clientNumber}/upload/presigned`,
          )

          const formData = new FormData()
          formData.append('key', presignedUrlResponse.fields.key)
          formData.append('success_action_status', '201')
          formData.append('Content-Type', f.type)
          formData.append(
            'X-Amz-Algorithm',
            presignedUrlResponse.fields['X-Amz-Algorithm'],
          )
          formData.append(
            'X-Amz-Credential',
            presignedUrlResponse.fields['X-Amz-Credential'],
          )
          formData.append(
            'X-Amz-Date',
            presignedUrlResponse.fields['X-Amz-Date'],
          )
          if (presignedUrlResponse.fields['X-Amz-Security-Token']) {
            formData.append(
              'X-Amz-Security-Token',
              presignedUrlResponse.fields['X-Amz-Security-Token'],
            )
          }
          formData.append(
            'X-Amz-Signature',
            presignedUrlResponse.fields['X-Amz-Signature'],
          )
          formData.append('Policy', presignedUrlResponse.fields.Policy)

          formData.append('file', f)

          const uploadRequest = await fetch(presignedUrlResponse.url, {
            method: 'POST',
            body: formData,
            signal: abortController.current.signal,
          })

          if (!uploadRequest.ok || uploadRequest.status !== 201) {
            throw new Error('Upload failed')
          }

          const responseXML = new DOMParser().parseFromString(
            await uploadRequest.text(),
            'text/xml',
          )

          const s3URL = responseXML.querySelector('Location')?.textContent

          if (!s3URL) {
            throw new Error('Upload failed')
          }

          dispatch([
            {
              type: MediaLibraryActions.SET_SEARCH_SEMANTIC_URL_VALUE,
              payload: s3URL,
            },
            {
              type: MediaLibraryActions.SET_SEARCH_SEMANTIC_VALUE,
              payload: '',
            },
            {
              type: MediaLibraryActions.SET_SORT_PARAM,
              payload: MLSortParams.RELEVANCE,
            },
            {
              type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
              payload: false,
            },
          ])
          setUploadState('idle')
        } catch (error) {
          setUploadState('error')
          setUploadError((error as any)?.message ?? error)
        } finally {
          dropzoneInstance.current?.enable()
        }

        // get the aws upload presigned url
      },
      drop: () => {
        offDropping()
      },
      dragenter: () => {
        onDropping()
      },
      dragleave: () => {
        offDropping()
      },
      dragend: () => {
        offDropping()
      },
    }

    const myDropZone = new Dropzone(
      '#semantic-search-dropzone',
      dropzoneOptions,
    )

    dropzoneInstance.current = myDropZone
  }, [])

  useEffect(() => {
    initializeDropzone() // initialize dropzone only on first render
  }, [initializeDropzone])

  const isInvalidURL = useMemo(() => {
    if (!url) {
      return false
    }
    try {
      void new URL(url)
      return false
    } catch {
      return true
    }
  }, [url])

  return (
    <>
      <HStack w="full">
        <IconButton
          aria-label="Close"
          variant="unstyled"
          size="xs"
          icon={<Icon as={IoIosArrowRoundBack} w="6" h="6" color="appSlate" />}
          _hover={{
            background: 'appPaleGreyTwo',
          }}
          onClick={() => {
            dispatch([
              {
                type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
                payload: false,
              },
            ])
          }}
        />
        <Text fontSize="14px" fontWeight="medium" color="appSlate">
          {t('search_by_image', 'Search by image')}
        </Text>
      </HStack>

      <Flex
        id="semantic-search-dropzone"
        h="18.857rem"
        w="full"
        background={
          uploadState === 'error' ? 'appVeryLightPink' : 'appPaleGreyTwo'
        }
        border="1px dashed"
        borderColor={
          isDropping
            ? 'appPaleCerulean'
            : uploadState === 'error'
            ? 'appWatermelon'
            : 'appLavenderBlue'
        }
        mx="1.143rem"
        borderRadius="4px"
        justifyContent="center"
        alignItems="center"
        cursor="pointer"
        role="button"
        direction="column"
        display={uploadState === 'loading' ? 'none' : 'flex'}
      >
        <SemanticSearchImageSearch boxSize="20" />
        {isDropping ? (
          <Trans t={t} i18nKey="drop_image_here">
            <Text pointerEvents="none">
              <Text
                as="span"
                color="appClearBlue"
                fontWeight="medium"
                ml="1"
                pointerEvents="none"
              >
                Drop your image here
              </Text>
            </Text>
          </Trans>
        ) : (
          <Trans t={t} i18nKey="drag_and_drop_image">
            <Text pointerEvents="none">Drag and drop image here to search</Text>
            <Text pointerEvents="none">
              or
              <Text
                as="span"
                color="appClearBlue"
                fontWeight="medium"
                ml="1"
                pointerEvents="none"
              >
                Upload Image
              </Text>
            </Text>
          </Trans>
        )}
      </Flex>
      {uploadState === 'loading' ? (
        <Flex
          h="18.857rem"
          w="full"
          background="appPaleGreyTwo"
          border="1px dashed"
          borderColor={isDropping ? 'appPaleCerulean' : 'appLavenderBlue'}
          mx="1.143rem"
          borderRadius="4px"
          justifyContent="center"
          alignItems="center"
          cursor="pointer"
          role="button"
          direction="column"
        >
          <Trans t={t} i18nKey="uploading">
            <Spinner
              size="lg"
              mb="4"
              speed="1s"
              color="appClearBlue"
              label="Uploading"
            />
            <Text
              as="span"
              color="appClearBlue"
              fontWeight="medium"
              ml="1"
              pointerEvents="none"
            >
              Uploading
            </Text>
          </Trans>
        </Flex>
      ) : null}
      <Text
        fontSize="14px"
        color="appWatermelon"
        mt="4"
        w="full"
        textAlign="left"
      >
        {uploadError}
      </Text>
      <Box position="relative" w="full" py="4">
        <Divider opacity="0.8" />
        <AbsoluteCenter bg="white" px="4">
          or
        </AbsoluteCenter>
      </Box>
      <Flex w="full" direction="column" gap="8px">
        <Text fontSize="14px" fontWeight="medium" color="appSlate">
          {t('upload_from_url', 'Upload from URL')}
        </Text>
        <HStack w="full" alignItems="flex-start">
          <FormControl isInvalid={isInvalidURL}>
            <Input
              placeholder={t('paste_image_url_here', 'Paste image URL here')}
              disabled={uploadState === 'loading'}
              value={url}
              onChange={e => void setUrl(e.target.value)}
            />
            <FormErrorMessage>Enter a valid URL.</FormErrorMessage>
          </FormControl>
          <ButtonGroup>
            <Button
              disabled={uploadState === 'loading' || isInvalidURL || !url}
              colorScheme="appBlue"
              onClick={() => {
                dispatch([
                  {
                    type: MediaLibraryActions.SET_SEARCH_SEMANTIC_URL_VALUE,
                    payload: url,
                  },
                  {
                    type: MediaLibraryActions.SET_SORT_PARAM,
                    payload: MLSortParams.RELEVANCE,
                  },
                  {
                    type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
                    payload: false,
                  },
                ])
              }}
            >
              {t('search', 'Search')}
            </Button>
            <Button
              variant="outline"
              colorScheme="appBlue"
              onClick={() => {
                setUrl('')
                abortController.current.abort()
                setSearchFocused(false)
                dispatch({
                  type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
                  payload: false,
                })
              }}
            >
              {t('cancel', 'Cancel')}
            </Button>
          </ButtonGroup>
        </HStack>
      </Flex>
    </>
  )
}
